import { Badge } from '@monksflow/monks-ui';
import { CheckIcon, ListChecksIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  chainStepsApprovedAnnouncement,
  chainStepsApprovedLabel,
  getChainStepProgress,
  type AssetApprovalChain,
  type ChainStepProgress,
} from '@/entities/asset-approval-chain';
import { DetailSidebarViewToggle } from '@/shared/ui';

import type { AssetApproval } from '../../hooks/useAssetApproval';
import { AssetApprovalCta } from '../AssetApprovalCta';

export interface AssetApprovalPrimaryProps {
  approval: AssetApproval;
  /** Null when the asset runs no chain; undefined while the read is still in flight. */
  chain: AssetApprovalChain | null | undefined;
  isLoadingChain: boolean;
  /** Omitted where there is no Approval rail to open, which leaves progress read-only. */
  rail?: { isOpen: boolean; onToggle: () => void };
}

/**
 * The rightmost header control: the binary approve toggle on an asset running no chain, the
 * chain's own progress on one that is.
 *
 * The two are exclusive rather than side by side. A chained asset reaches approved by clearing
 * every step, and the API refuses a status write on one, so a binary toggle here would only
 * collect conflicts.
 */
export function AssetApprovalPrimary(props: AssetApprovalPrimaryProps) {
  if (props.isLoadingChain) {
    return <AssetApprovalCta approval={{ ...props.approval, isUpdating: true }} />;
  }

  if (!props.chain) {
    return <AssetApprovalCta approval={props.approval} />;
  }

  const progress = getChainStepProgress(props.chain);

  if (progress.isComplete) {
    return <CompleteChainProgress rail={props.rail} />;
  }

  if (!props.rail) {
    return (
      <Badge
        variant="warning"
        roundness="full"
        aria-label={chainStepsApprovedAnnouncement(progress)}
        data-testid="asset-approval-progress"
      >
        <ListChecksIcon />
        {chainStepsApprovedLabel(progress)}
      </Badge>
    );
  }

  return <RunningChainToggle progress={progress} rail={props.rail} />;
}

function RunningChainToggle(props: {
  progress: ChainStepProgress;
  rail: { isOpen: boolean; onToggle: () => void };
}) {
  const { t } = useTranslation('projects');

  const announcement = chainStepsApprovedAnnouncement(props.progress);

  return (
    <DetailSidebarViewToggle
      icon={<ListChecksIcon className="size-4" />}
      view="approval"
      openView={props.rail.isOpen ? 'approval' : undefined}
      onToggle={props.rail.onToggle}
      className="bg-warning px-4 text-warning-foreground hover:bg-warning/80 hover:text-warning-foreground data-[state=on]:bg-warning data-[state=on]:text-warning-foreground"
      label={chainStepsApprovedLabel(props.progress)}
      showLabel={t('projectDetail.assetApprovalPrimary.showApprovalPanelWithProgress', {
        progress: announcement,
      })}
      hideLabel={t('projectDetail.assetApprovalPrimary.hideApprovalPanelWithProgress', {
        progress: announcement,
      })}
      dataTestId="asset-approval-progress-toggle"
    />
  );
}

function CompleteChainProgress(props: { rail?: { isOpen: boolean; onToggle: () => void } }) {
  const { t } = useTranslation('projects');
  const { t: tCommon } = useTranslation('common');

  const approvedLabel = tCommon('ui.approvalActionButton.approved');

  if (!props.rail) {
    return (
      <Badge variant="positive" roundness="full" data-testid="asset-approval-progress">
        <CheckIcon />
        {approvedLabel}
      </Badge>
    );
  }

  return (
    <div
      className="flex h-10 items-stretch overflow-hidden rounded-md bg-positive text-positive-foreground"
      data-testid="asset-approval-progress"
    >
      <span className="flex items-center gap-1 pl-3 pr-2 text-sm font-medium">
        <CheckIcon className="size-5" aria-hidden />
        {approvedLabel}
      </span>
      <span className="w-px bg-positive-foreground/30" aria-hidden />
      <DetailSidebarViewToggle
        size="default"
        icon={<ListChecksIcon className="size-4" />}
        view="approval"
        openView={props.rail.isOpen ? 'approval' : undefined}
        onToggle={props.rail.onToggle}
        className="h-auto rounded-none bg-transparent text-positive-foreground hover:bg-positive-foreground/15 hover:text-positive-foreground data-[state=on]:bg-transparent data-[state=on]:text-positive-foreground"
        showLabel={t('projectDetail.assetSidebar.showApprovalPanel')}
        hideLabel={t('projectDetail.assetSidebar.hideApprovalPanel')}
        dataTestId="asset-approval-progress-toggle"
      />
    </div>
  );
}

AssetApprovalPrimary.displayName = 'AssetApprovalPrimary';
